import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Plus, Search, Edit3, Trash2, EyeOff, Eye, ExternalLink,
  ArrowUp, ArrowDown, Layers, X, Save, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import { getFeatureIcon } from "@/data/featureIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

/* -------------------- Groups drawer -------------------- */
function GroupsDrawer({ open, onClose, groups, refresh }) {
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ slug: "", title: "", description: "", order: 0 });
  const [savingSlug, setSavingSlug] = useState(null);

  if (!open) return null;

  const handleCreate = async () => {
    if (!newForm.title.trim()) return toast.error("Group title is required.");
    setCreating(true);
    try {
      const payload = { ...newForm, slug: newForm.slug || slugify(newForm.title) };
      await axios.post(`${API}/admin/feature-groups`, payload);
      toast.success("Group created.");
      setNewForm({ slug: "", title: "", description: "", order: 0 });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally {
      setCreating(false);
    }
  };

  const handlePatch = async (slug, patch) => {
    setSavingSlug(slug);
    try {
      await axios.patch(`${API}/admin/feature-groups/${slug}`, patch);
      toast.success("Saved.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSavingSlug(null);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete group "${slug}"? Features in this group will need to be reassigned.`)) return;
    try {
      await axios.delete(`${API}/admin/feature-groups/${slug}`);
      toast.success("Group deleted.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="admin-groups-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-sky-600" />
            <h2 className="text-base font-bold text-slate-900">Feature Groups</h2>
            <span className="text-xs text-slate-400">({groups.length})</span>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-groups-drawer-close"
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Existing groups */}
          <div className="space-y-3">
            {groups.map((g) => (
              <GroupCard
                key={g.slug}
                group={g}
                busy={savingSlug === g.slug}
                onSave={(patch) => handlePatch(g.slug, patch)}
                onDelete={() => handleDelete(g.slug)}
              />
            ))}
            {groups.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-6">
                No groups yet. Create your first group below.
              </p>
            )}
          </div>

          {/* New group form */}
          <div className="border-t border-slate-200 pt-6">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Add new group</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">Title *</Label>
                <Input
                  value={newForm.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setNewForm({ ...newForm, title, slug: newForm.slug || slugify(title) });
                  }}
                  placeholder="e.g. Marketing"
                  data-testid="admin-groups-new-title"
                  className="mt-1 h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">URL Slug *</Label>
                <Input
                  value={newForm.slug}
                  onChange={(e) => setNewForm({ ...newForm, slug: slugify(e.target.value) })}
                  placeholder="e.g. marketing"
                  data-testid="admin-groups-new-slug"
                  className="mt-1 h-9 text-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Description</Label>
                <Textarea
                  value={newForm.description}
                  onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                  placeholder="Short tagline shown under the group heading"
                  rows={2}
                  data-testid="admin-groups-new-desc"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Display order (lower = higher on page)</Label>
                <Input
                  type="number"
                  value={newForm.order}
                  onChange={(e) => setNewForm({ ...newForm, order: Number(e.target.value) || 0 })}
                  data-testid="admin-groups-new-order"
                  className="mt-1 h-9 text-sm w-24"
                />
              </div>
              <Button
                onClick={handleCreate}
                disabled={creating}
                data-testid="admin-groups-new-save"
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs h-9"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {creating ? "Creating…" : "Add group"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GroupCard({ group, busy, onSave, onDelete }) {
  const [form, setForm] = useState({
    title: group.title,
    description: group.description || "",
    order: group.order ?? 0,
  });
  const dirty =
    form.title !== group.title ||
    form.description !== (group.description || "") ||
    form.order !== (group.order ?? 0);

  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`admin-group-card-${group.slug}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono text-slate-400 uppercase">/{group.slug}</p>
        </div>
        <button
          onClick={onDelete}
          disabled={busy}
          data-testid={`admin-group-delete-${group.slug}`}
          className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
          title="Delete group"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <Input
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Title"
        data-testid={`admin-group-title-${group.slug}`}
        className="h-8 text-sm"
      />
      <Textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Description (optional)"
        rows={2}
        data-testid={`admin-group-desc-${group.slug}`}
        className="text-sm"
      />
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={form.order}
          onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
          data-testid={`admin-group-order-${group.slug}`}
          className="h-8 text-xs w-20"
        />
        <span className="text-[10px] text-slate-400 flex-1">Display order</span>
        <Button
          disabled={!dirty || busy}
          onClick={() => onSave(form)}
          data-testid={`admin-group-save-${group.slug}`}
          size="sm"
          className="bg-sky-600 hover:bg-sky-700 text-white text-[10px] h-7 rounded-md px-3"
        >
          <Save className="h-3 w-3 mr-1" /> {busy ? "…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

/* -------------------- Main features list page -------------------- */
export default function AdminFeaturesPage() {
  const [features, setFeatures] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [groupsOpen, setGroupsOpen] = useState(false);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [featsRes, groupsRes] = await Promise.all([
        axios.get(`${API}/admin/features`),
        axios.get(`${API}/admin/feature-groups`),
      ]);
      setFeatures(featsRes.data || []);
      setGroups(groupsRes.data || []);
    } catch {
      toast.error("Failed to load features.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const groupMap = useMemo(() => {
    const m = {};
    for (const g of groups) m[g.slug] = g;
    return m;
  }, [groups]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return features.filter((f) => {
      if (groupFilter !== "all" && f.group_slug !== groupFilter) return false;
      if (!q) return true;
      return (
        f.name.toLowerCase().includes(q) ||
        f.slug.toLowerCase().includes(q) ||
        (f.short_desc || "").toLowerCase().includes(q)
      );
    });
  }, [features, query, groupFilter]);

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete feature "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/features/${slug}`);
      setFeatures((prev) => prev.filter((f) => f.slug !== slug));
      toast.success("Feature deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const handleTogglePublish = async (f) => {
    try {
      const { data } = await axios.patch(`${API}/admin/features/${f.slug}`, {
        published: !f.published,
      });
      setFeatures((prev) => prev.map((x) => (x.slug === f.slug ? { ...x, ...data } : x)));
      toast.success(data.published ? "Published." : "Unpublished.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const handleReorder = async (f, direction) => {
    const inGroup = filtered
      .filter((x) => x.group_slug === f.group_slug)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = inGroup.findIndex((x) => x.slug === f.slug);
    const swap = direction === "up" ? inGroup[idx - 1] : inGroup[idx + 1];
    if (!swap) return;
    try {
      await Promise.all([
        axios.patch(`${API}/admin/features/${f.slug}`, { order: swap.order ?? 0 }),
        axios.patch(`${API}/admin/features/${swap.slug}`, { order: f.order ?? 0 }),
      ]);
      fetchAll();
    } catch {
      toast.error("Reorder failed.");
    }
  };

  return (
    <div data-testid="admin-features-page" className="bg-white min-h-full">
      <Toaster position="top-right" richColors />

      <div className="px-5 lg:px-8 py-5 border-b border-slate-200 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <Sparkles className="h-4 w-4 text-sky-600" />
            <h1 className="text-base font-bold">Features</h1>
            <span className="text-xs text-slate-400">({features.length})</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Manage the feature cards on /features and their optional detail pages.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search features…"
              className="pl-9 h-9 w-56 text-xs"
              data-testid="admin-features-search"
            />
          </div>
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            data-testid="admin-features-group-filter"
            className="h-9 px-3 text-xs border border-slate-300 rounded-md bg-white"
          >
            <option value="all">All groups</option>
            {groups.map((g) => (
              <option key={g.slug} value={g.slug}>{g.title}</option>
            ))}
          </select>
          <Button
            variant="outline"
            onClick={() => setGroupsOpen(true)}
            data-testid="admin-features-manage-groups"
            className="rounded-lg text-xs h-9"
          >
            <Layers className="h-3.5 w-3.5 mr-1" /> Manage groups
          </Button>
          <Button
            onClick={() => navigate("/admin/features/new")}
            data-testid="admin-features-new"
            className="bg-sky-600 hover:bg-sky-700 text-white h-9 rounded-lg text-xs font-semibold"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> New feature
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
            <p className="text-sm text-slate-500">
              {features.length === 0 ? "No features yet. Add your first one." : "No features match your search."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm" data-testid="admin-features-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold w-24">Order</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Feature</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Group</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Detail Page</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filtered.map((f, i) => {
                  const Icon = getFeatureIcon(f.icon);
                  const group = groupMap[f.group_slug];
                  return (
                    <tr key={f.slug} data-testid={`admin-features-row-${f.slug}`} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-mono text-slate-500 w-6">{f.order ?? 0}</span>
                          <button
                            onClick={() => handleReorder(f, "up")}
                            disabled={i === 0}
                            data-testid={`admin-features-up-${f.slug}`}
                            className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleReorder(f, "down")}
                            disabled={i === filtered.length - 1}
                            data-testid={`admin-features-down-${f.slug}`}
                            className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2.5">
                          <span className="h-8 w-8 shrink-0 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center">
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900">{f.name}</p>
                            <p className="text-[10px] text-slate-400 font-mono mt-0.5 line-clamp-1">/features/{f.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {group ? group.title : <span className="text-red-500">Missing</span>}
                      </td>
                      <td className="px-4 py-3">
                        {f.has_detail_page ? (
                          <span className="text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                            <ChevronRight className="h-2.5 w-2.5" /> On
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">Off</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {f.published ? (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                            <Eye className="h-2.5 w-2.5" /> Published
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                            <EyeOff className="h-2.5 w-2.5" /> Draft
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {f.has_detail_page && (
                            <Link
                              to={`/features/${f.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View detail page"
                              className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                          )}
                          <button
                            onClick={() => handleTogglePublish(f)}
                            title={f.published ? "Unpublish" : "Publish"}
                            data-testid={`admin-features-toggle-${f.slug}`}
                            className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          >
                            {f.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                          <Link
                            to={`/admin/features/${f.slug}`}
                            data-testid={`admin-features-edit-${f.slug}`}
                            className="p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </Link>
                          <button
                            onClick={() => handleDelete(f.slug)}
                            data-testid={`admin-features-delete-${f.slug}`}
                            className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GroupsDrawer
        open={groupsOpen}
        onClose={() => setGroupsOpen(false)}
        groups={groups}
        refresh={fetchAll}
      />
    </div>
  );
}
